import { useEffect, useRef, useState } from 'preact/hooks';
import type { ChatMessage, ServerMessage } from '../../shared/types';
import { user } from '../state/session';
import { liveItem, activeLiveChat, liveChatOpen } from '../state/player';
import {
  type ChatProtocolAdapter,
  type ChatUser,
  IrcAdapter,
  NostrAdapter,
  MatrixAdapter,
  generateAnonymousNick,
  sanitizeNick,
  senderColor,
} from '../lib/chat';
import { StreamBadge } from './StreamBadge';
import { Icon } from './Icon';
import { isDesktop } from '../lib/media';

interface LiveChatProps {
  feedId: number;
  guid: string;
  onClose?: () => void;
}

interface ReactionBurst {
  id: string;
  emoji: string;
  leftOffset: number; // percentage (10% to 90%)
}

const REACTION_EMOJIS = ['🔥', '👏', '❤️', '🎙️'];

export function LiveChat({ feedId, guid, onClose }: LiveChatProps) {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [listenerCount, setListenerCount] = useState<number>(1);
  const [channelUsers, setChannelUsers] = useState<ChatUser[]>([]);
  const [userSearch, setUserSearch] = useState('');
  const [inputText, setInputText] = useState('');
  const [bursts, setBursts] = useState<ReactionBurst[]>([]);
  const [connected, setConnected] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState<string>('connecting');
  const [isEnded, setIsEnded] = useState(false);
  const [chatError, setChatError] = useState<string | null>(null);

  // Lurk mode & nick management
  const [currentNick, setCurrentNick] = useState<string>(() => {
    try {
      return (
        localStorage.getItem(`podcastie_chat_nick_${feedId}`) ||
        localStorage.getItem('podcastie_chat_nick') ||
        generateAnonymousNick()
      );
    } catch {
      return generateAnonymousNick();
    }
  });
  const [hasJoined, setHasJoined] = useState<boolean>(() => {
    try {
      return (
        localStorage.getItem(`podcastie_chat_joined_${feedId}`) === 'true' ||
        localStorage.getItem('podcastie_chat_joined') === 'true'
      );
    } catch {
      return false;
    }
  });
  const [isJoinModalOpen, setIsJoinModalOpen] = useState(false);
  const [joinNickInput, setJoinNickInput] = useState('');
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);

  const listRef = useRef<HTMLOListElement>(null);
  const wsRef = useRef<WebSocket | null>(null);
  const adapterRef = useRef<ChatProtocolAdapter | null>(null);
  const isUserScrolledUp = useRef(false);
  const reconnectTimeoutRef = useRef<number | undefined>(undefined);
  const reconnectAttempts = useRef(0);
  const isManuallyClosed = useRef(false);

  // Auto-scroll to bottom if user has not scrolled up
  const scrollToBottom = () => {
    if (listRef.current && !isUserScrolledUp.current) {
      listRef.current.scrollTop = listRef.current.scrollHeight;
    }
  };

  const handleScroll = () => {
    if (!listRef.current) return;
    const { scrollTop, scrollHeight, clientHeight } = listRef.current;
    // Considered scrolled up if more than 30px from bottom
    const distanceFromBottom = scrollHeight - (scrollTop + clientHeight);
    isUserScrolledUp.current = distanceFromBottom > 30;
  };

  const triggerReactionBurst = (emoji: string) => {
    const id = `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
    const leftOffset = Math.floor(Math.random() * 80) + 10;
    setBursts((prev) => [...prev, { id, emoji, leftOffset }]);

    setTimeout(() => {
      setBursts((prev) => prev.filter((b) => b.id !== id));
    }, 2500);
  };

  const chatConfig = liveItem.value?.chat;
  const rawProtocol = chatConfig?.protocol?.toLowerCase();
  const isExternal = Boolean(
    chatConfig &&
      rawProtocol &&
      rawProtocol !== 'native' &&
      (rawProtocol === 'irc' || rawProtocol === 'nostr' || rawProtocol === 'matrix')
  );
  const protocol = isExternal ? rawProtocol! : 'native';

  useEffect(() => {
    isManuallyClosed.current = false;
    let pingInterval: number | undefined;

    if (isExternal) {
      // External multi-protocol adapter
      let adapter: ChatProtocolAdapter;
      if (protocol === 'irc') {
        adapter = new IrcAdapter();
      } else if (protocol === 'nostr') {
        adapter = new NostrAdapter();
      } else {
        adapter = new MatrixAdapter();
      }
      adapterRef.current = adapter;

      adapter.onMessage = (msg: ChatMessage) => {
        setMessages((prev) => [...prev, msg]);
        setTimeout(scrollToBottom, 0);
      };

      adapter.onStatus = (status: 'connecting' | 'connected' | 'disconnected' | 'error', detail?: string) => {
        setConnectionStatus(status);
        if (status === 'connected') {
          setConnected(true);
        } else if (status === 'disconnected') {
          setConnected(false);
        } else if (status === 'error') {
          setConnected(false);
          if (detail) {
            setChatError(detail);
            setTimeout(() => setChatError(null), 3000);
          }
        }
      };

      adapter.onError = (err: Error) => {
        setChatError(err.message || 'Connection error');
        setTimeout(() => setChatError(null), 3000);
      };

      adapter.onUserCount = (count: number) => {
        setListenerCount(count);
      };

      adapter.onUsers = (users: ChatUser[]) => {
        setChannelUsers(users);
      };

      adapter
        .connect({
          feedId,
          guid,
          server: chatConfig?.server,
          channel: chatConfig?.space || chatConfig?.accountId,
          space: chatConfig?.space,
          nick: currentNick,
        })
        .then(() => {
          if (adapter.currentNick) {
            setCurrentNick(adapter.currentNick);
          }
        })
        .catch(() => {
          // Handled via adapter.onError/onStatus
        });

      return () => {
        isManuallyClosed.current = true;
        if (adapterRef.current) {
          adapterRef.current.disconnect();
          adapterRef.current = null;
        }
      };
    }

    // Native LiveRoom Durable Object WebSocket connection
    const connect = () => {
      if (isManuallyClosed.current) return;

      const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      const host = window.location.host;
      const url = `${wsProtocol}//${host}/api/live/${feedId}/${encodeURIComponent(guid)}/ws`;

      setConnectionStatus('connecting');
      const ws = new WebSocket(url);
      wsRef.current = ws;

      ws.onopen = () => {
        setConnected(true);
        setConnectionStatus('connected');
        reconnectAttempts.current = 0;
        // Periodic ping
        pingInterval = window.setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) {
            ws.send(JSON.stringify({ type: 'ping' }));
          }
        }, 30_000);
      };

      ws.onmessage = (event) => {
        let data: ServerMessage;
        try {
          data = JSON.parse(event.data as string);
        } catch {
          return;
        }

        switch (data.type) {
          case 'room_state': {
            setListenerCount(data.listenerCount);
            if (Array.isArray(data.recentMessages)) {
              setMessages(data.recentMessages);
            }
            setTimeout(scrollToBottom, 0);
            break;
          }
          case 'chat': {
            const newMsg: ChatMessage = {
              id: data.id,
              text: data.text,
              displayName: data.displayName,
              timestamp: data.timestamp,
              protocol: data.protocol,
              senderColor: data.senderColor,
            };
            setMessages((prev) => [...prev, newMsg]);
            setTimeout(scrollToBottom, 0);
            break;
          }
          case 'reaction': {
            triggerReactionBurst(data.emoji);
            break;
          }
          case 'listener_count': {
            setListenerCount(data.count);
            break;
          }
          case 'status_change': {
            if (data.status === 'ended') {
              setIsEnded(true);
            }
            break;
          }
          case 'error': {
            setChatError(data.message);
            setTimeout(() => setChatError(null), 3000);
            break;
          }
          default:
            break;
        }
      };

      ws.onclose = () => {
        setConnected(false);
        setConnectionStatus('disconnected');
        if (pingInterval !== undefined) {
          clearInterval(pingInterval);
          pingInterval = undefined;
        }
        if (!isManuallyClosed.current) {
          // Exponential backoff reconnect: 1s, 2s, 4s, up to 10s
          const delay = Math.min(1000 * Math.pow(2, reconnectAttempts.current), 10_000);
          reconnectAttempts.current += 1;
          reconnectTimeoutRef.current = window.setTimeout(connect, delay);
        }
      };

      ws.onerror = () => {
        // ws.onclose will trigger reconnect
      };
    };

    connect();

    return () => {
      isManuallyClosed.current = true;
      if (pingInterval !== undefined) {
        clearInterval(pingInterval);
      }
      if (reconnectTimeoutRef.current !== undefined) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      if (wsRef.current) {
        wsRef.current.close();
      }
    };
  }, [feedId, guid, protocol, chatConfig?.server, chatConfig?.space, chatConfig?.accountId]);

  const handleSendMessage = async (e: Event) => {
    e.preventDefault();
    const text = inputText.trim();
    if (!text) return;

    if (isExternal && adapterRef.current) {
      try {
        await adapterRef.current.sendMessage(text);
        setInputText('');
      } catch (err: any) {
        setChatError(err.message || 'Failed to send message');
        setTimeout(() => setChatError(null), 3000);
      }
      return;
    }

    if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
      return;
    }

    const displayName = hasJoined ? (currentNick || user.value?.displayName || undefined) : (user.value?.displayName || undefined);
    wsRef.current.send(
      JSON.stringify({
        type: 'chat',
        text,
        displayName,
      }),
    );
    setInputText('');
  };

  const handleSendReaction = (emoji: string) => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(
        JSON.stringify({
          type: 'reaction',
          emoji,
        }),
      );
    }
    triggerReactionBurst(emoji);
  };

  const handleOpenJoin = () => {
    let suggested = currentNick;
    if (user.value?.displayName) {
      suggested = sanitizeNick(user.value.displayName);
    }
    setJoinNickInput(suggested);
    setIsJoinModalOpen(true);
  };

  const handleConfirmJoin = async (e: Event) => {
    e.preventDefault();
    const confirmedNick = sanitizeNick(joinNickInput || currentNick);
    setCurrentNick(confirmedNick);
    setHasJoined(true);
    try {
      localStorage.setItem(`podcastie_chat_nick_${feedId}`, confirmedNick);
      localStorage.setItem(`podcastie_chat_joined_${feedId}`, 'true');
      localStorage.setItem('podcastie_chat_nick', confirmedNick);
      localStorage.setItem('podcastie_chat_joined', 'true');
    } catch {
      // Ignore localStorage errors
    }

    if (adapterRef.current) {
      await adapterRef.current.changeNick(confirmedNick);
    }

    setIsJoinModalOpen(false);
  };

  const formatMessageTime = (ts: number) => {
    const d = new Date(ts);
    return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  // Header channel pill computation
  const channelOrSpace = chatConfig?.space || chatConfig?.accountId || '';
  const protocolUpper = protocol.toUpperCase();

  // For Nostr coordinates like "30311:pubkey:identifier", show a clean user-facing title
  let pillTitle = 'Live Stream';
  if (isExternal) {
    if (protocol === 'nostr') {
      pillTitle = 'Nostr Live Chat';
    } else if (channelOrSpace) {
      const formattedCh = channelOrSpace.startsWith('#') || channelOrSpace.startsWith('&')
        ? channelOrSpace
        : `#${channelOrSpace}`;
      pillTitle = `${formattedCh} (${protocolUpper})`;
    } else {
      pillTitle = protocolUpper;
    }
  }

  // Parse Nostr coordinate parts if available
  const isNostrCoord = protocol === 'nostr' && channelOrSpace.includes(':');
  let nostrPubkey = '';
  let nostrIdentifier = '';
  if (isNostrCoord) {
    const parts = channelOrSpace.split(':');
    if (parts.length >= 3) {
      nostrPubkey = parts[1];
      nostrIdentifier = parts.slice(2).join(':');
    }
  }

  return (
    <div class="live-chat" role="region" aria-label="Live episode chat">
      {/* Header with channel pill, listener count & metadata popover */}
      <div class="live-chat__header">
        <button
          type="button"
          class="live-chat__channel-pill"
          onClick={() => setIsPopoverOpen((prev) => !prev)}
          aria-expanded={isPopoverOpen}
          aria-label="Toggle channel details"
        >
          <span
            class={`live-chat__dot ${connected ? 'is-connected' : 'is-disconnected'}`}
            aria-hidden="true"
          />
          <span class="live-chat__channel-name">{pillTitle}</span>
          <span class="live-chat__pill-sep" aria-hidden="true">·</span>
          <span class="live-chat__pill-listeners">
            <span aria-hidden="true">👥</span> {listenerCount}
          </span>
        </button>
        <div class="live-chat__header-actions">
          <StreamBadge />
          {onClose && (
            <button
              type="button"
              class="icon-button live-chat__header-close"
              onClick={onClose}
              aria-label="Close live chat"
              title="Close chat"
            >
              <Icon name="x" size={18} />
            </button>
          )}
        </div>
      </div>

      {/* Metadata Popover */}
      {isPopoverOpen && (
        <div class="live-chat__popover" role="dialog" aria-label="Channel information">
          <div class="live-chat__popover-row">
            <span class="live-chat__popover-label">Protocol:</span>
            <span class="live-chat__popover-value">
              {protocol === 'nostr' ? 'Nostr (NIP-53 Live Activity)' : protocolUpper}
            </span>
          </div>
          {chatConfig?.server && (
            <div class="live-chat__popover-row">
              <span class="live-chat__popover-label">
                {protocol === 'nostr' ? 'Relay:' : 'Server:'}
              </span>
              <span class="live-chat__popover-value">{chatConfig.server}</span>
            </div>
          )}
          {isNostrCoord ? (
            <>
              {nostrIdentifier && (
                <div class="live-chat__popover-row">
                  <span class="live-chat__popover-label">Stream ID:</span>
                  <span class="live-chat__popover-value" title={nostrIdentifier}>
                    {nostrIdentifier.length > 24 ? `${nostrIdentifier.slice(0, 16)}...` : nostrIdentifier}
                  </span>
                </div>
              )}
              {nostrPubkey && (
                <div class="live-chat__popover-row">
                  <span class="live-chat__popover-label">Host Pubkey:</span>
                  <span class="live-chat__popover-value" title={nostrPubkey}>
                    {nostrPubkey.slice(0, 8)}...{nostrPubkey.slice(-6)}
                  </span>
                </div>
              )}
            </>
          ) : (
            channelOrSpace && (
              <div class="live-chat__popover-row">
                <span class="live-chat__popover-label">Space / Channel:</span>
                <span class="live-chat__popover-value">{channelOrSpace}</span>
              </div>
            )
          )}
          <div class="live-chat__popover-row">
            <span class="live-chat__popover-label">Status:</span>
            <span class="live-chat__popover-value">{connectionStatus}</span>
          </div>

          {/* User / Listener Roster */}
          <div class="live-chat__roster">
            <div class="live-chat__roster-header">
              <span class="live-chat__roster-title">
                Listeners ({channelUsers.length > 0 ? channelUsers.length : listenerCount})
              </span>
              {channelUsers.length > 5 && (
                <input
                  type="search"
                  class="live-chat__roster-search"
                  placeholder="Filter users..."
                  value={userSearch}
                  onInput={(e) => setUserSearch((e.target as HTMLInputElement).value)}
                  aria-label="Filter listeners"
                />
              )}
            </div>

            {channelUsers.length > 0 ? (
              <ul class="live-chat__roster-list" role="list">
                {channelUsers
                  .filter((u) => !userSearch || u.nick.toLowerCase().includes(userSearch.toLowerCase()))
                  .map((u) => {
                    const isSelf = u.nick === currentNick;
                    return (
                      <li key={u.nick} class="live-chat__roster-item">
                        <span
                          class="live-chat__roster-avatar"
                          style={{ backgroundColor: senderColor(u.nick) }}
                          aria-hidden="true"
                        >
                          {u.nick.slice(0, 1).toUpperCase()}
                        </span>
                        <span class="live-chat__roster-nick" title={u.nick}>
                          {u.nick} {isSelf && <span class="muted">(you)</span>}
                        </span>
                        <span class="live-chat__roster-badges">
                          {u.isOp && (
                            <span class="live-chat__badge live-chat__badge--op" title="Channel Operator (@)">
                              OP
                            </span>
                          )}
                          {u.isVoice && !u.isOp && (
                            <span class="live-chat__badge live-chat__badge--voice" title="Voiced Member (+)">
                              VOICE
                            </span>
                          )}
                        </span>
                      </li>
                    );
                  })}
              </ul>
            ) : (
              <p class="live-chat__roster-empty muted">
                {protocol === 'irc' ? 'Loading user list...' : `${listenerCount} listener${listenerCount === 1 ? '' : 's'} connected`}
              </p>
            )}
          </div>

          <button
            type="button"
            class="live-chat__popover-close button button--outline"
            onClick={() => setIsPopoverOpen(false)}
          >
            Close
          </button>
        </div>
      )}

      {/* External chat room info banner if specified in podcast:chat */}
      {chatConfig?.server && (
        <div class="live-chat__external-notice">
          <span>External room on {chatConfig.protocol || 'chat'}: </span>
          <a
            href={
              chatConfig.server.startsWith('http')
                ? chatConfig.server
                : `https://${chatConfig.server}`
            }
            target="_blank"
            rel="noopener noreferrer"
            class="live-chat__external-link"
          >
            {chatConfig.server}
          </a>
        </div>
      )}

      {/* Stream ended notice */}
      {isEnded && (
        <div class="live-chat__ended-notice">
          <span>Broadcast has concluded. </span>
          <a href={`/podcasts/${feedId}`} class="live-chat__archive-link">
            Return to episode archive
          </a>
        </div>
      )}

      {/* Ephemeral error notice (e.g. rate limit) */}
      {chatError && (
        <div class="live-chat__error-notice" role="alert">
          {chatError}
        </div>
      )}

      {/* Floating reaction bursts overlay */}
      <div class="live-chat__burst-container" aria-hidden="true">
        {bursts.map((burst) => (
          <span
            key={burst.id}
            class="live-chat__burst"
            style={{ left: `${burst.leftOffset}%` }}
          >
            {burst.emoji}
          </span>
        ))}
      </div>

      {/* Message history */}
      <ol
        ref={listRef}
        class="live-chat__messages"
        onScroll={handleScroll}
        aria-live="polite"
        aria-label="Chat messages"
      >
        {messages.length === 0 ? (
          <li class="live-chat__empty muted">Welcome to the live chat! Say something to get started.</li>
        ) : (
          messages.map((msg) => {
            const author = msg.displayName || 'Anonymous';
            const color = msg.senderColor || senderColor(author);
            const msgProtocol = msg.protocol && msg.protocol !== 'native' ? msg.protocol : undefined;
            return (
              <li key={msg.id} class="live-chat__message">
                <div class="live-chat__message-header">
                  <div class="live-chat__author-group">
                    <span class="live-chat__author" style={{ color }}>
                      {author}
                    </span>
                    {msgProtocol && (
                      <span class={`live-chat__badge live-chat__badge--${msgProtocol}`}>
                        {msgProtocol.toUpperCase()}
                      </span>
                    )}
                  </div>
                  <time class="live-chat__time" dateTime={new Date(msg.timestamp).toISOString()}>
                    {formatMessageTime(msg.timestamp)}
                  </time>
                </div>
                <p class="live-chat__text">{msg.text}</p>
              </li>
            );
          })
        )}
      </ol>

      {/* Ephemeral Reaction Tray (always accessible even in lurk mode) */}
      <div class="live-chat__reactions-tray" role="group" aria-label="Quick reactions">
        {REACTION_EMOJIS.map((emoji) => (
          <button
            key={emoji}
            type="button"
            class="live-chat__reaction-btn"
            onClick={() => handleSendReaction(emoji)}
            aria-label={`Send reaction ${emoji}`}
          >
            {emoji}
          </button>
        ))}
      </div>

      {/* Lurk Banner or Full Message Input Form */}
      {!hasJoined ? (
        <div class="live-chat__lurk-banner">
          <span class="live-chat__lurk-text">
            👀 Viewing as <strong>{currentNick}</strong>
          </span>
          <button
            type="button"
            class="live-chat__join-btn button button--primary"
            onClick={handleOpenJoin}
          >
            Join the conversation
          </button>
        </div>
      ) : (
        <form class="live-chat__form" onSubmit={handleSendMessage}>
          <input
            type="text"
            class="live-chat__input input"
            placeholder={`Message as ${currentNick}…`}
            value={inputText}
            onInput={(e) => setInputText((e.target as HTMLInputElement).value)}
            aria-label="Message text"
            maxLength={500}
          />
          <button
            type="submit"
            class="live-chat__submit button button--primary"
            disabled={!inputText.trim()}
            aria-label="Send message"
          >
            Send
          </button>
        </form>
      )}

      {/* Join Conversation Inline Drawer / Modal */}
      {isJoinModalOpen && (
        <div class="live-chat__join-modal-backdrop" role="dialog" aria-label="Join Conversation">
          <form class="live-chat__join-form" onSubmit={handleConfirmJoin}>
            <h3 class="live-chat__join-title">Join the Conversation</h3>
            <p class="live-chat__join-subtitle">Pick a nickname to send messages in live chat.</p>
            <div class="live-chat__join-field">
              <label for="chat-nick-input" class="live-chat__join-label">
                Nickname:
              </label>
              <input
                id="chat-nick-input"
                type="text"
                class="input live-chat__join-input"
                value={joinNickInput}
                onInput={(e) => {
                  const val = (e.target as HTMLInputElement).value;
                  setJoinNickInput(val);
                }}
                maxLength={16}
                required
              />
            </div>
            <div class="live-chat__join-actions">
              <button
                type="button"
                class="live-chat__join-cancel button button--outline"
                onClick={() => setIsJoinModalOpen(false)}
              >
                Cancel
              </button>
              <button
                type="submit"
                class="live-chat__join-confirm button button--primary"
                disabled={!joinNickInput.trim()}
              >
                Confirm & Join
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}

/**
 * Top-level persistent live chat component mounted in App.tsx.
 * On desktop (>= 1024px), it renders as a persistent right-side sidebar column.
 * On narrower viewports, it renders as a slide-up drawer without tearing down on route changes.
 */
export function PersistentLiveChat() {
  const chat = activeLiveChat.value;
  const isOpen = liveChatOpen.value;
  const drawerRef = useRef<HTMLDivElement>(null);

  if (!chat || !isOpen) return null;

  const close = () => {
    liveChatOpen.value = false;
  };

  if (isDesktop.value) {
    return (
      <aside
        class="live-chat-sidebar"
        role="complementary"
        aria-label={`Live chat for ${chat.title}`}
      >
        <LiveChat feedId={chat.feedId} guid={chat.guid} onClose={close} />
      </aside>
    );
  }

  return (
    <div
      class="live-chat-drawer-backdrop"
      role="dialog"
      aria-modal="true"
      aria-label={`Live chat drawer for ${chat.title}`}
      onClick={(e) => {
        if (e.target === e.currentTarget) close();
      }}
    >
      <div ref={drawerRef} class="live-chat-drawer" tabIndex={-1}>
        <div class="live-chat-drawer__content">
          <LiveChat feedId={chat.feedId} guid={chat.guid} onClose={close} />
        </div>
      </div>
    </div>
  );
}
